import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import {
    cancelSubscription,
    createCheckoutSession,
    createPortalSession,
    endTrial,
    resumeSubscription,
} from "../api/mutations/billing";
import {
    getBillingOverview,
    getCheckoutSessionStatus,
    getCreditsEstimate,
    getCreditsStatus,
    getCreditsSummary,
    getUsageLedger,
} from "../api/queries/billing";
import {
    BILLING_OVERVIEW,
    CHECKOUT_SESSION_STATUS,
    CREDITS_ESTIMATE,
    CREDITS_STATUS,
    CREDITS_SUMMARY,
    USAGE_LEDGER,
} from "./utils/constants";

export const useCreateCheckoutSession = () => {
    return useMutation(createCheckoutSession)
}

export const useSessionStatus = (sessionId, options = {}) => {
    return useQuery(
        [CHECKOUT_SESSION_STATUS, sessionId],
        () => getCheckoutSessionStatus(sessionId),
        {
            enabled: !!sessionId,
            retry: false,
            ...options,
        }
    )
}

/*
 * ROAR-4943 — the Credits & Usage data layer (AC1, AC2, AC3).
 *
 * No company discriminator in any of these query keys: Settings is gated to customer roles
 * (ROAR-4899 §4 decision 6), so a user's company is fixed for the session and there is nothing to
 * invalidate on. Do not reintroduce one.
 */

/**
 * Credit lots and available balance for the Account Credits block.
 *
 * @param {object} [options] Extra react-query options.
 * @returns {import('react-query').UseQueryResult}
 */
export const useCreditsSummary = (options = {}) =>
    useQuery([CREDITS_SUMMARY], () => getCreditsSummary(), options);

/**
 * Paged usage ledger.
 *
 * Server-paginated via `useInfiniteQuery` + a `useInView` sentinel in `UsageLedger.js`, because
 * `components/Table/index.js` registers no pagination plugin. The search term and all three facets
 * are PART OF THE QUERY KEY, so changing any of them refetches from page 1 rather than showing the
 * previous facet's accumulated rows; `keepPreviousData` avoids a flash while that happens.
 *
 * `getNextPageParam` follows hooks/cicd.js: trust an explicit `has_more` when the backend sends
 * one — in both directions, since a cursor-paged backend may return a short page and still have
 * more — otherwise infer from a short page.
 *
 * Each page also carries `filters`, the filter panel's option lists — the backend serves them on
 * this endpoint rather than a second one. They are identical on every page and are NOT narrowed by
 * the facets in the query key, so a consumer reads them off any page; `UsageLedger.js` takes the
 * first and holds the last it saw, since an errored read has no pages to read them from.
 *
 * @param {object} [options]
 * @param {number} [options.pageSize] Rows per page.
 * @param {string} [options.search] Free-text member/activity search.
 * @param {string} [options.from] Inclusive start date, `YYYY-MM-DD`.
 * @param {string} [options.to] Inclusive end date, `YYYY-MM-DD`.
 * @param {string} [options.userId] Member facet.
 * @param {string} [options.activityType] Activity-type facet.
 * @returns {import('react-query').UseInfiniteQueryResult}
 */
export const useUsageLedger = (options = {}) => {
    const { pageSize = 25, search, from, to, userId, activityType } = options;
    const filters = { search, from, to, userId, activityType };

    return useInfiniteQuery(
        [USAGE_LEDGER, { pageSize, ...filters }],
        ({ pageParam = 1 }) => getUsageLedger({ page: pageParam, pageSize, ...filters }),
        {
            getNextPageParam: (lastPage, allPages) => {
                // An explicit flag from the backend always wins.
                if (lastPage?.has_more !== undefined && lastPage?.has_more !== null) {
                    return lastPage.has_more ? (allPages?.length || 1) + 1 : undefined;
                }
                // Otherwise a short (or empty) page means we have reached the end.
                const items = lastPage?.items || [];
                if (items.length < pageSize) return undefined;
                return (allPages?.length || 1) + 1;
            },
            keepPreviousData: true,
        }
    );
};

/*
 * ROAR-4944 — the Payments & Invoices data layer (AC4, AC5, AC6, AC7).
 *
 * Same no-company-discriminator rule as the Credits hooks above (§4 decision 6).
 */

/**
 * Plan + payment-method summary.
 *
 * @param {object} [options] Extra react-query options.
 * @returns {import('react-query').UseQueryResult}
 */
export const useBillingOverview = (options = {}) =>
    useQuery([BILLING_OVERVIEW], () => getBillingOverview(), options);

/**
 * Creates a Stripe Customer Portal session for either button (AC5, AC6).
 *
 * Deliberately does NOT invalidate `BILLING_OVERVIEW`: creating a session changes nothing on our
 * side, and whatever the user does inside the portal arrives later via P1.12's webhooks.
 *
 * @returns {import('react-query').UseMutationResult}
 */
export const useCreatePortalSession = () => useMutation(createPortalSession);

/**
 * Requests cancellation at period end (AC7).
 *
 * Invalidates `BILLING_OVERVIEW` on success AND on a **409** — per §4 decision 16 a 409 means a
 * cancellation was already pending, so our cached overview is provably stale and the pane must
 * refetch into the pending state. Any other error is a genuine failure and leaves the cache alone:
 * refetching there would replace a real error with a silent, unchanged view.
 *
 * The rejection is re-thrown untouched so the caller can still tell 409 from 500.
 *
 * @returns {import('react-query').UseMutationResult}
 */
export const useCancelSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation(cancelSubscription, {
        onSuccess: () => queryClient.invalidateQueries(BILLING_OVERVIEW),
        onError: (error) => {
            if (error?.response?.status === 409) {
                queryClient.invalidateQueries(BILLING_OVERVIEW);
            }
        },
    });
};

/**
 * Withdraws a pending cancellation (§4 decision 16).
 *
 * @returns {import('react-query').UseMutationResult}
 */
export const useResumeSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation(resumeSubscription, {
        onSuccess: () => queryClient.invalidateQueries(BILLING_OVERVIEW),
    });
};

/**
 * ROAR-5007 — ends the trial now, from the credits badge's "Start Early Now".
 *
 * Invalidates the badge status and the Settings billing reads on success AND on a 404: a 404 means
 * there is no trialing subscription any more (converted elsewhere), so the badge offering the link
 * is provably stale. The credit grant itself lands later via webhook, which the badge's own poll
 * (`CREDITS_STATUS_REFETCH_MS`) picks up.
 *
 * @returns {import('react-query').UseMutationResult}
 */
export const useEndTrial = () => {
    const queryClient = useQueryClient();
    const refresh = () => {
        queryClient.invalidateQueries(CREDITS_STATUS);
        queryClient.invalidateQueries(CREDITS_SUMMARY);
        queryClient.invalidateQueries(BILLING_OVERVIEW);
    };
    return useMutation(endTrial, {
        onSuccess: refresh,
        onError: (error) => {
            if (error?.response?.status === 404) refresh();
        },
    });
};

/*
 * ROAR-5001 (P1.14) — the top-nav credits badge data layer (AC1-AC5, AC18).
 *
 * Again no company discriminator in the keys, but for a different reason than the Settings hooks
 * above: these DO follow the MSSP company selection (they keep the scope headers), and switching
 * company in `GlobalCompanySelection` hard-reloads the page, which drops the whole cache. A key
 * discriminator would only matter if that switch ever became a soft one.
 */

/** How often the badge re-reads while the tab is open (AC1). */
export const CREDITS_STATUS_REFETCH_MS = 60 * 1000;

/**
 * Balance, cycle, trial and `banner_state` for the company in scope.
 *
 * AC1 asks the badge to refresh after any reserve, settle or release. Reserve happens on our own
 * scan-start mutations, which invalidate `CREDITS_STATUS` directly; settle and release happen
 * server-side whenever a scan finishes, with no event the FE can hear — so this also polls, and
 * re-reads on window focus (the app-wide default turns that off).
 *
 * @param {object} [options] Extra react-query options.
 * @returns {import('react-query').UseQueryResult}
 */
export const useCreditsStatus = (options = {}) =>
    useQuery([CREDITS_STATUS], () => getCreditsStatus(), {
        refetchInterval: CREDITS_STATUS_REFETCH_MS,
        refetchOnWindowFocus: true,
        ...options,
    });

/**
 * A credit quote for one feature (AC6, AC10). With no `params`, quotes the default settings.
 *
 * `params` is part of the key so a form that re-quotes on input change never shows a stale price.
 *
 * @param {string} featureKey A `billing.features` catalog key.
 * @param {object} [params] Pricing inputs; omit for the feature's default settings.
 * @param {object} [options] Extra react-query options.
 * @returns {import('react-query').UseQueryResult}
 */
export const useCreditsEstimate = (featureKey, params, options = {}) =>
    useQuery(
        [CREDITS_ESTIMATE, featureKey, params ?? null],
        () => getCreditsEstimate({ featureKey, params }),
        { enabled: !!featureKey, ...options }
    );

